<template>
  <div class="calculo-flow-editor d-flex" @click="closeContextMenu">
    <flow-sidebar>
      <flow-sidebar-title>{{ flowAddNodeLabel }}</flow-sidebar-title>
      <flow-sidebar-item
        v-for="item in sidebarItems"
        :key="item.tipo"
        :accent="item.accent"
        type="button"
        @click="addNodeAtCenter(item.tipo)"
      >
        <v-icon small :color="item.accent">{{ item.icon }}</v-icon>
        <span>{{ item.label }}</span>
      </flow-sidebar-item>
      <v-divider class="my-3" />
      <div class="text-caption grey--text">{{ flowHintLabel }}</div>
    </flow-sidebar>

    <div class="calculo-flow-main flex-grow-1 d-flex flex-column">
      <div class="calculo-flow-toolbar d-flex align-center px-3 py-2">
        <v-btn icon x-small @click="zoomOut"><v-icon small>mdi-minus</v-icon></v-btn>
        <span class="mx-2 text-caption">{{ Math.round(viewport.zoom * 100) }}%</span>
        <v-btn icon x-small @click="zoomIn"><v-icon small>mdi-plus</v-icon></v-btn>
        <v-btn icon x-small class="ml-1" @click="resetViewport"><v-icon small>mdi-fit-to-screen</v-icon></v-btn>
        <v-btn icon x-small class="ml-1" @click="autoLayout"><v-icon small>mdi-auto-fix</v-icon></v-btn>
        <v-spacer />
        <v-chip v-if="selectedNodeId" x-small outlined close @click:close="selectedNodeId = null">
          {{ flowSelectedLabel }}
        </v-chip>
        <v-btn
          v-if="selectedNodeId && canDeleteSelected"
          icon
          x-small
          color="error"
          class="ml-2"
          @click="deleteSelectedNode"
        >
          <v-icon small>mdi-delete</v-icon>
        </v-btn>
      </div>

      <flow-canvas
        ref="canvas"
        class="flex-grow-1"
        @wheel.prevent="onWheel"
        @contextmenu.prevent="onCanvasContextMenu"
      >
        <flow-transform-layer ref="transformLayer" :style="transformStyle">
          <div class="flow-pane-bg" @mousedown="onCanvasPanStart" />
          <flow-edges-svg :style="svgSizeStyle">
            <defs>
              <marker
                id="flow-arrow"
                markerWidth="8"
                markerHeight="8"
                refX="0"
                refY="3"
                orient="auto"
                markerUnits="userSpaceOnUse"
              >
                <path d="M0,0 L7,3 L0,6 Z" fill="#78909c" />
              </marker>
            </defs>
            <path
              v-for="edge in flowEdges"
              :key="edge.id"
              :d="edgePath(edge)"
              fill="none"
              stroke="#78909c"
              stroke-width="2"
              marker-end="url(#flow-arrow)"
            />
            <path
              v-if="connectingLine"
              :d="connectingLine"
              fill="none"
              stroke="#1976d2"
              stroke-width="2"
              stroke-dasharray="6 4"
            />
          </flow-edges-svg>

          <flow-node-shell
            v-for="node in flowNodes"
            :key="node.id"
            :accent="nodeAccent(node)"
            :class="{ 'is-selected': selectedNodeId === node.id }"
            :style="nodeStyle(node)"
            @mousedown.stop="onNodeShellMouseDown($event, node)"
            @contextmenu.prevent.stop="onNodeContextMenu($event, node)"
          >
            <div class="flow-node-anchor" :data-flow-node-id="node.id">
            <flow-node-header :accent="nodeAccent(node)">
              <v-icon x-small dark>{{ nodeIcon(node) }}</v-icon>
              <span>{{ nodeTitle(node) }}</span>
              <v-spacer />
              <v-btn
                v-if="node.type !== 'output'"
                icon
                x-small
                dark
                @click.stop="deleteNode(node.id)"
              >
                <v-icon x-small>mdi-close</v-icon>
              </v-btn>
            </flow-node-header>

            <flow-node-body>
              <template v-if="node.type === 'data'">
                <v-select
                  v-model="node.data.tipo_passo"
                  :items="dataStepTypeOptions"
                  item-text="text"
                  item-value="value"
                  dense
                  hide-details
                  filled
                  class="mb-2 flow-field"
                  @change="onDataTipoChange(node)"
                />
                <template v-if="node.data.tipo_passo === 'OBTER_GRANDEZA'">
                  <v-autocomplete
                    v-model="grandezaDef(node).id_variavel"
                    :items="variaveisGrandeza"
                    item-text="codigo"
                    item-value="id_variavel"
                    :label="$t('subsidyMotorCatalog.fields.variable')"
                    dense
                    hide-details
                    filled
                    class="mb-1 flow-field"
                    @input="emitPassos"
                  >
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>{{ item.codigo }}</v-list-item-title>
                        <v-list-item-subtitle>{{ item.descricao }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                  <v-select
                    v-model="grandezaDef(node).funcao_agregacao"
                    :items="funcaoAgregacaoOptions"
                    item-text="text"
                    item-value="value"
                    :label="$t('subsidyMotorCatalog.fields.aggregation')"
                    dense
                    hide-details
                    filled
                    class="mb-1 flow-field"
                    @input="emitPassos"
                  />
                  <v-select
                    v-model="grandezaDef(node).id_agrupamento"
                    :items="agrupamentos"
                    item-text="codigo"
                    item-value="id_agrupamento"
                    :label="$t('subsidyMotorCatalog.fields.grouping')"
                    dense
                    hide-details
                    filled
                    class="flow-field"
                    @input="emitPassos"
                  />
                  <div
                    v-if="grandezaResumo(node)"
                    class="text-caption grey--text mt-1 flow-grandeza-resumo"
                  >
                    {{ grandezaResumo(node) }}
                  </div>
                </template>
                <v-select
                  v-else-if="node.data.tipo_passo === 'OBTER_VARIAVEL_CONTRATO'"
                  v-model="node.data.config.id_variavel"
                  :items="variaveisContrato"
                  item-text="codigo"
                  item-value="id_variavel"
                  dense
                  hide-details
                  filled
                  class="flow-field"
                  @input="emitPassos"
                />
                <v-text-field
                  v-else-if="node.data.tipo_passo === 'OBTER_PARAMETRO'"
                  :value="constanteInputValue(node)"
                  :label="$t('subsidyMotorCatalog.fields.constant')"
                  dense
                  hide-details
                  outlined
                  class="flow-field"
                  @input="onConstanteInput(node, $event)"
                />
                <v-text-field
                  v-model="node.data.saida_alias"
                  :label="$t('subsidyMotorCatalog.fields.outputAlias')"
                  :rules="[aliasRule]"
                  dense
                  hide-details="auto"
                  outlined
                  class="mt-2 flow-field flow-alias"
                  @input="onAliasChange"
                />
              </template>

              <template v-else-if="node.type === 'comparison' || node.type === 'arithmetic'">
                <v-select
                  v-model="node.data.config.operador"
                  :items="node.type === 'comparison' ? operadorComparacaoOptions : operadorAritmeticoOptions"
                  item-text="text"
                  item-value="value"
                  dense
                  hide-details
                  filled
                  class="mb-2 flow-field flow-operator"
                  @input="emitPassos"
                />
                <div class="flow-operand-hints text-caption grey--text">
                  <span v-if="operandLabel(node, 'left')">{{ operandLabel(node, "left") }}</span>
                  <span v-else class="flow-operand-empty">{{ flowConnectLeftLabel }}</span>
                  <span class="mx-1">{{ operadorSymbol(node) }}</span>
                  <span v-if="operandLabel(node, 'right')">{{ operandLabel(node, "right") }}</span>
                  <span v-else class="flow-operand-empty">{{ flowConnectRightLabel }}</span>
                </div>
                <v-text-field
                  v-model="node.data.saida_alias"
                  :label="$t('subsidyMotorCatalog.fields.outputAlias')"
                  :rules="[aliasRule]"
                  dense
                  hide-details="auto"
                  outlined
                  class="mt-2 flow-field flow-alias"
                  @input="onAliasChange"
                />
              </template>

              <template v-else-if="node.type === 'logic'">
                <v-select
                  v-model="node.data.config.operador"
                  :items="operadorLogicoOptions"
                  item-text="text"
                  item-value="value"
                  dense
                  hide-details
                  filled
                  class="mb-2 flow-field flow-operator-logic"
                  @change="onLogicOperadorChange(node)"
                />
                <div v-if="node.data.config.operador === 'NOT'" class="text-caption grey--text">
                  <span v-if="operandLabel(node, 'in')">{{ operandLabel(node, "in") }}</span>
                  <span v-else class="flow-operand-empty">{{ flowConnectInputLabel }}</span>
                </div>
                <div v-else-if="logicOperands(node).length" class="text-caption">
                  <v-chip
                    v-for="(op, idx) in logicOperands(node)"
                    :key="`${node.id}-op-${idx}`"
                    x-small
                    label
                    class="mr-1 mb-1"
                  >
                    {{ op }}
                  </v-chip>
                </div>
                <div v-else class="text-caption flow-operand-empty">
                  {{ flowConnectInputsLabel }}
                </div>
                <v-text-field
                  v-model="node.data.saida_alias"
                  :label="$t('subsidyMotorCatalog.fields.outputAlias')"
                  :rules="[aliasRule]"
                  dense
                  hide-details="auto"
                  outlined
                  class="mt-2 flow-field flow-alias"
                  @input="onAliasChange"
                />
              </template>

              <template v-else-if="node.type === 'output'">
                <div class="flow-output-label text-h6 font-weight-medium text-center">
                  {{ outputDisplayLabel(node) }}
                </div>
                <div class="text-caption text-center grey--text mt-1">
                  {{ flowFinalResultLabel }}
                </div>
              </template>
            </flow-node-body>

            <div
              v-if="node.type !== 'output'"
              class="flow-handle-wrap"
              data-flow-handle="true"
              :data-node-id="node.id"
              data-handle-id="out"
              data-handle-role="source"
            >
              <flow-handle
                :active="isHandleActive(node.id, 'out', 'source')"
                @mousedown.stop.prevent="onHandleMouseDown($event, node.id, 'out', 'source')"
              />
            </div>
            <template v-if="node.type === 'comparison' || node.type === 'arithmetic'">
              <div
                class="flow-handle-wrap flow-handle-wrap--left"
                data-flow-handle="true"
                :data-node-id="node.id"
                data-handle-id="left"
                data-handle-role="target"
              >
                <flow-handle
                  :active="isHandleActive(node.id, 'left', 'target')"
                  @mousedown.stop.prevent="onHandleMouseDown($event, node.id, 'left', 'target')"
                />
              </div>
              <div
                class="flow-handle-wrap flow-handle-wrap--right"
                data-flow-handle="true"
                :data-node-id="node.id"
                data-handle-id="right"
                data-handle-role="target"
              >
                <flow-handle
                  :active="isHandleActive(node.id, 'right', 'target')"
                  @mousedown.stop.prevent="onHandleMouseDown($event, node.id, 'right', 'target')"
                />
              </div>
            </template>
            <template v-else-if="node.type === 'logic'">
              <div
                v-if="node.data.config.operador === 'NOT'"
                class="flow-handle-wrap flow-handle-wrap--in"
                data-flow-handle="true"
                :data-node-id="node.id"
                data-handle-id="in"
                data-handle-role="target"
              >
                <flow-handle
                  :active="isHandleActive(node.id, 'in', 'target')"
                  @mousedown.stop.prevent="onHandleMouseDown($event, node.id, 'in', 'target')"
                />
              </div>
              <div
                v-for="idx in logicInputCount(node)"
                v-else
                :key="`${node.id}-in-${idx - 1}`"
                class="flow-handle-wrap flow-handle-wrap--in"
                :style="{ top: `${28 + (idx - 1) * 22}px`, marginTop: 0 }"
                data-flow-handle="true"
                :data-node-id="node.id"
                :data-handle-id="`in-${idx - 1}`"
                data-handle-role="target"
              >
                <flow-handle
                  :active="isHandleActive(node.id, `in-${idx - 1}`, 'target')"
                  @mousedown.stop.prevent="onHandleMouseDown($event, node.id, `in-${idx - 1}`, 'target')"
                />
              </div>
            </template>
            <div
              v-if="node.type === 'output'"
              class="flow-handle-wrap flow-handle-wrap--in"
              data-flow-handle="true"
              :data-node-id="node.id"
              data-handle-id="in"
              data-handle-role="target"
            >
              <flow-handle
                :active="isHandleActive(node.id, 'in', 'target')"
                @mousedown.stop.prevent="onHandleMouseDown($event, node.id, 'in', 'target')"
              />
            </div>
            </div>
          </flow-node-shell>
        </flow-transform-layer>
      </flow-canvas>
    </div>

    <flow-context-menu
      v-if="contextMenu.visible"
      :style="{ left: `${contextMenu.x}px`, top: `${contextMenu.y}px` }"
      @click.stop
    >
      <flow-context-menu-item
        v-for="item in sidebarItems"
        :key="`ctx-${item.tipo}`"
        :accent="item.accent"
        type="button"
        @click="addNodeFromContext(item.tipo)"
      >
        {{ item.label }}
      </flow-context-menu-item>
    </flow-context-menu>
  </div>
</template>

<script>
import {
  FlowCanvas,
  FlowTransformLayer,
  FlowEdgesSvg,
  FlowNodeShell,
  FlowNodeHeader,
  FlowNodeBody,
  FlowHandle,
  FlowSidebar,
  FlowSidebarTitle,
  FlowSidebarItem,
  FlowContextMenu,
  FlowContextMenuItem,
  accentForNodeType,
} from "./flowStyled";
import {
  passosToFlow,
  flowToPassos,
  layoutFlowGraph,
  createFlowNodeFromTipo,
  createEmptyOutputNode,
  defaultPassoConfig,
  flowNodeTypeFromPasso,
} from "../../../utils/subsidioCalculoFlowConverter";

const NODE_DIM = {
  data: { w: 220, h: 130 },
  comparison: { w: 200, h: 120 },
  arithmetic: { w: 200, h: 120 },
  logic: { w: 260, h: 130 },
  output: { w: 180, h: 72 },
};

export default {
  name: "CalculoFlowEditor",
  components: {
    FlowCanvas,
    FlowTransformLayer,
    FlowEdgesSvg,
    FlowNodeShell,
    FlowNodeHeader,
    FlowNodeBody,
    FlowHandle,
    FlowSidebar,
    FlowSidebarTitle,
    FlowSidebarItem,
    FlowContextMenu,
    FlowContextMenuItem,
  },
  props: {
    value: { type: Array, default: () => [] },
    // Variáveis que podem formar grandeza (as de contrato têm passo próprio).
    variaveisGrandeza: { type: Array, default: () => [] },
    agrupamentos: { type: Array, default: () => [] },
    funcaoAgregacaoOptions: { type: Array, default: () => [] },
    // Agrupamento sugerido para a grandeza de um nó novo: o do cálculo.
    defaultAgrupamentoId: { type: [Number, String], default: null },
    variaveisContrato: { type: Array, default: () => [] },
    outputLabel: { type: String, default: "resultado" },
    tipoPassoOptions: { type: Array, default: () => [] },
    operadorComparacaoOptions: { type: Array, default: () => [] },
    operadorAritmeticoOptions: { type: Array, default: () => [] },
    operadorLogicoOptions: { type: Array, default: () => [] },
    aliasRule: { type: Function, default: () => () => true },
  },
  data() {
    return {
      flowNodes: [],
      flowEdges: [],
      viewport: { x: 0, y: 0, zoom: 1 },
      selectedNodeId: null,
      draggingNode: null,
      panning: false,
      panStart: null,
      connecting: null,
      connectMouse: null,
      contextMenu: { visible: false, x: 0, y: 0, flowX: 0, flowY: 0 },
      suppressPassosWatch: false,
      edgeLayoutKey: 0,
    };
  },
  computed: {
    flowAddNodeLabel() {
      return this.$te("subsidyMotorCatalog.flow.addNode") ? this.$t("subsidyMotorCatalog.flow.addNode") : "Adicionar nó";
    },
    flowHintLabel() {
      return this.$te("subsidyMotorCatalog.flow.hint")
        ? this.$t("subsidyMotorCatalog.flow.hint")
        : "Arraste das portas para conectar. Clique com o botão direito para adicionar.";
    },
    flowSelectedLabel() {
      return this.$te("subsidyMotorCatalog.flow.selected") ? this.$t("subsidyMotorCatalog.flow.selected") : "Selecionado";
    },
    flowConnectLeftLabel() {
      return this.$te("subsidyMotorCatalog.flow.connectLeft") ? this.$t("subsidyMotorCatalog.flow.connectLeft") : "conectar esquerda";
    },
    flowConnectRightLabel() {
      return this.$te("subsidyMotorCatalog.flow.connectRight") ? this.$t("subsidyMotorCatalog.flow.connectRight") : "conectar direita";
    },
    flowConnectInputLabel() {
      return this.$te("subsidyMotorCatalog.flow.connectInput") ? this.$t("subsidyMotorCatalog.flow.connectInput") : "conectar entrada";
    },
    flowConnectInputsLabel() {
      return this.$te("subsidyMotorCatalog.flow.connectInputs") ? this.$t("subsidyMotorCatalog.flow.connectInputs") : "conectar entradas";
    },
    flowFinalResultLabel() {
      return this.$te("subsidyMotorCatalog.flow.finalResult") ? this.$t("subsidyMotorCatalog.flow.finalResult") : "Resultado final";
    },
    transformStyle() {
      const { x, y, zoom } = this.viewport;
      return { transform: `translate(${x}px, ${y}px) scale(${zoom})` };
    },
    svgSizeStyle() {
      return { width: "4000px", height: "3000px" };
    },
    connectingLine() {
      if (!this.connecting || !this.connectMouse) return null;
      const start = this.handlePosition(this.connecting.nodeId, this.connecting.handleId, this.connecting.role);
      if (!start) return null;
      return `M ${start.x} ${start.y} L ${this.connectMouse.x} ${this.connectMouse.y}`;
    },
    dataStepTypeOptions() {
      return (this.tipoPassoOptions || []).filter((o) =>
        ["OBTER_GRANDEZA", "OBTER_VARIAVEL_CONTRATO", "OBTER_PARAMETRO"].includes(o.value)
      );
    },
    sidebarItems() {
      return [
        { tipo: "OBTER_GRANDEZA", label: this.$t("subsidyMotorCatalog.stepTypes.obtainMagnitude"), icon: "mdi-database-outline", accent: accentForNodeType("data") },
        { tipo: "OBTER_VARIAVEL_CONTRATO", label: this.$t("subsidyMotorCatalog.stepTypes.obtainContractVariable"), icon: "mdi-file-document-outline", accent: accentForNodeType("data") },
        { tipo: "OBTER_PARAMETRO", label: this.$t("subsidyMotorCatalog.stepTypes.obtainConstant"), icon: "mdi-tune-variant", accent: accentForNodeType("data") },
        { tipo: "COMPARAR", label: this.$t("subsidyMotorCatalog.stepTypes.compare"), icon: "mdi-compare-horizontal", accent: accentForNodeType("comparison") },
        { tipo: "ARITMETICO", label: this.$t("subsidyMotorCatalog.stepTypes.arithmetic"), icon: "mdi-calculator-variant-outline", accent: accentForNodeType("arithmetic") },
        { tipo: "LOGICO", label: this.$t("subsidyMotorCatalog.stepTypes.logical"), icon: "mdi-set-center", accent: accentForNodeType("logic") },
      ];
    },
    canDeleteSelected() {
      return this.selectedNodeId && this.selectedNodeId !== "flow-output";
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(passos) {
        if (this.suppressPassosWatch) return;
        this.loadFromPassos(passos);
      },
    },
    defaultAgrupamentoId() {
      const antes = JSON.stringify(this.flowNodes.map((n) => n.data.config));
      this.flowNodes.forEach((node) => this.ensureGrandezaDef(node));
      if (JSON.stringify(this.flowNodes.map((n) => n.data.config)) !== antes) this.emitPassos();
    },
    outputLabel(label) {
      const output = this.flowNodes.find((n) => n.type === "output");
      if (output) output.data.label = label || "resultado";
    },
  },
  mounted() {
    window.addEventListener("mousemove", this.onWindowMouseMove);
    window.addEventListener("mouseup", this.onWindowMouseUp);
    window.addEventListener("resize", this.refreshEdgeLayout);
    this.$nextTick(() => this.refreshEdgeLayout());
  },
  beforeDestroy() {
    window.removeEventListener("mousemove", this.onWindowMouseMove);
    window.removeEventListener("mouseup", this.onWindowMouseUp);
    window.removeEventListener("resize", this.refreshEdgeLayout);
    if (this._edgeResizeObserver) this._edgeResizeObserver.disconnect();
  },
  methods: {
    loadFromPassos(passos) {
      const safe = Array.isArray(passos) && passos.length ? passos : [createFlowNodeFromTipo("OBTER_GRANDEZA", 0).data];
      const normalized = safe.map((p, idx) => ({
        _uid: p._uid || `passo-${idx}`,
        tipo_passo: p.tipo_passo || "OBTER_GRANDEZA",
        config: { ...(p.config || defaultPassoConfig(p.tipo_passo)) },
        saida_alias: p.saida_alias || "",
        ordem: p.ordem != null ? p.ordem : idx,
      }));
      const graph = passosToFlow(normalized, this.outputLabel);
      graph.nodes.forEach((node) => this.ensureGrandezaDef(node));
      this.flowNodes = graph.nodes;
      this.flowEdges = graph.edges;
      this.$nextTick(() => {
        this.fitView();
        this.refreshEdgeLayout();
        this.observeNodeResize();
      });
    },
    refreshEdgeLayout() {
      this.$nextTick(() => {
        this.edgeLayoutKey += 1;
      });
    },
    observeNodeResize() {
      if (typeof ResizeObserver === "undefined") return;
      if (this._edgeResizeObserver) this._edgeResizeObserver.disconnect();
      const layer = this.getTransformLayerEl();
      if (!layer) return;
      this._edgeResizeObserver = new ResizeObserver(() => this.refreshEdgeLayout());
      this._edgeResizeObserver.observe(layer);
    },
    getTransformLayerEl() {
      const ref = this.$refs.transformLayer;
      return ref && ref.$el ? ref.$el : ref;
    },
    emitPassos() {
      this.suppressPassosWatch = true;
      const passos = flowToPassos(this.flowNodes, this.flowEdges);
      this.$emit("input", passos);
      this.$nextTick(() => {
        this.suppressPassosWatch = false;
      });
    },
    nodeAccent(node) {
      return accentForNodeType(node.type);
    },
    nodeStyle(node) {
      return {
        left: `${node.position.x}px`,
        top: `${node.position.y}px`,
        width: `${(NODE_DIM[node.type] || NODE_DIM.data).w}px`,
      };
    },
    nodeIcon(node) {
      const icons = {
        data: "mdi-database-outline",
        comparison: "mdi-compare-horizontal",
        arithmetic: "mdi-calculator-variant-outline",
        logic: "mdi-set-center",
        output: "mdi-flag-checkered",
      };
      return icons[node.type] || "mdi-circle-outline";
    },
    nodeTitle(node) {
      if (node.type === "output") {
        return this.$te("subsidyMotorCatalog.flow.output") ? this.$t("subsidyMotorCatalog.flow.output") : "Saída";
      }
      const found = (this.tipoPassoOptions || []).find((o) => o.value === node.data.tipo_passo);
      return found ? found.text : node.data.tipo_passo;
    },
    outputDisplayLabel(node) {
      const incoming = this.flowEdges.find((e) => e.target === node.id);
      if (incoming) {
        const src = this.flowNodes.find((n) => n.id === incoming.source);
        if (src && src.data.saida_alias) return src.data.saida_alias;
      }
      return node.data.label || this.outputLabel;
    },
    constanteInputValue(node) {
      const cfg = (node.data && node.data.config) || {};
      if (cfg.valor_constante != null && cfg.valor_constante !== "") return String(cfg.valor_constante);
      return cfg.param_key || "";
    },
    onConstanteInput(node, raw) {
      // O campo aceita tanto um número digitado direto quanto o nome de um
      // parâmetro do contrato; o número vira constante e dispensa cadastro.
      const texto = String(raw == null ? "" : raw).trim();
      const numero = texto.replace(",", ".");
      if (texto !== "" && numero !== "" && Number.isFinite(Number(numero))) {
        node.data.config = { param_key: null, valor_constante: Number(numero) };
      } else {
        node.data.config = { param_key: texto, valor_constante: null };
      }
      this.emitPassos();
    },
    onDataTipoChange(node) {
      node.data.config = defaultPassoConfig(node.data.tipo_passo);
      this.ensureGrandezaDef(node);
      node.type = flowNodeTypeFromPasso(node.data.tipo_passo);
      this.emitPassos();
      this.refreshEdgeLayout();
    },
    onLogicOperadorChange(node) {
      const op = node.data.config.operador;
      if (op === "NOT") {
        node.data.config = { operador: "NOT", operand_alias: null };
      } else if (op === "AND" || op === "OR") {
        node.data.config = { operador: op, operands: [] };
      } else {
        node.data.config = { operador: op, left_alias: null, right_alias: null };
      }
      this.flowEdges = this.flowEdges.filter((e) => e.target !== node.id);
      this.emitPassos();
    },
    onAliasChange() {
      this.emitPassos();
    },
    /**
     * Garante a definição da grandeza no nó (passo antigo sem definição, nó
     * recém-criado ou trocado de tipo) e sugere o agrupamento do cálculo.
     * Roda fora do render: mutar o nó durante o render dispararia novo render.
     */
    ensureGrandezaDef(node) {
      if (!node || node.data.tipo_passo !== "OBTER_GRANDEZA") return;
      const config = node.data.config || {};
      if (!config.grandeza || typeof config.grandeza !== "object") {
        node.data.config = {
          ...config,
          grandeza: { id_variavel: null, funcao_agregacao: "SOMA", id_agrupamento: this.defaultAgrupamentoId },
        };
      } else if (config.grandeza.id_agrupamento == null && this.defaultAgrupamentoId != null) {
        this.$set(config.grandeza, "id_agrupamento", this.defaultAgrupamentoId);
      }
    },
    grandezaDef(node) {
      return (node.data.config && node.data.config.grandeza) || {};
    },
    grandezaResumo(node) {
      const def = node.data.config && node.data.config.grandeza;
      if (!def || !def.id_variavel) return "";
      const variavel = (this.variaveisGrandeza || []).find((v) => v.id_variavel === def.id_variavel);
      const agrupamento = (this.agrupamentos || []).find((a) => a.id_agrupamento === def.id_agrupamento);
      const nomeVariavel = variavel ? variavel.codigo : `#${def.id_variavel}`;
      return `${def.funcao_agregacao || "?"}(${nomeVariavel})${agrupamento ? ` / ${agrupamento.codigo}` : ""}`;
    },
    logicInputCount(node) {
      const op = node.data.config.operador;
      if (op !== "AND" && op !== "OR") return 0;
      const connected = this.flowEdges.filter((e) => e.target === node.id && String(e.targetHandle || "").startsWith("in-")).length;
      return Math.max(2, connected + 1);
    },
    logicOperands(node) {
      const labels = [];
      this.flowEdges
        .filter((e) => e.target === node.id)
        .forEach((e) => {
          const src = this.flowNodes.find((n) => n.id === e.source);
          if (src && src.data.saida_alias) labels.push(src.data.saida_alias);
        });
      return labels;
    },
    operandLabel(node, handle) {
      const edge = this.flowEdges.find((e) => e.target === node.id && e.targetHandle === handle);
      if (!edge) return "";
      const src = this.flowNodes.find((n) => n.id === edge.source);
      return src && src.data.saida_alias ? src.data.saida_alias : "";
    },
    operadorSymbol(node) {
      const op = node.data.config.operador;
      const list =
        node.type === "comparison"
          ? this.operadorComparacaoOptions
          : node.type === "arithmetic"
            ? this.operadorAritmeticoOptions
            : this.operadorLogicoOptions;
      const found = (list || []).find((o) => o.value === op);
      return found ? found.text : op;
    },
    resolveHandleId(handleId, role) {
      if (role === "source" || handleId === "out") return "out";
      return handleId || "in";
    },
    handlePosition(nodeId, handleId, role) {
      void this.edgeLayoutKey;
      const node = this.flowNodes.find((n) => n.id === nodeId);
      if (!node) return null;
      const resolvedId = this.resolveHandleId(handleId, role);
      const layer = this.getTransformLayerEl();
      const anchor = layer && layer.querySelector(`[data-flow-node-id="${nodeId}"]`);
      const handleWrap = anchor && anchor.querySelector(`[data-handle-id="${resolvedId}"]`);
      if (handleWrap) {
        return {
          x: node.position.x + handleWrap.offsetLeft + handleWrap.offsetWidth / 2,
          y: node.position.y + handleWrap.offsetTop + handleWrap.offsetHeight / 2,
        };
      }
      return this.handlePositionFallback(nodeId, resolvedId);
    },
    handlePositionFallback(nodeId, handleId) {
      const node = this.flowNodes.find((n) => n.id === nodeId);
      if (!node) return null;
      const layer = this.getTransformLayerEl();
      const anchor = layer && layer.querySelector(`[data-flow-node-id="${nodeId}"]`);
      const shell = anchor && anchor.parentElement;
      const height = anchor ? anchor.offsetHeight : (NODE_DIM[node.type] || NODE_DIM.data).h;
      const width = shell ? shell.offsetWidth : (NODE_DIM[node.type] || NODE_DIM.data).w;
      const x = node.position.x;
      const y = node.position.y;
      if (handleId === "out") {
        return { x: x + width + 1, y: y + height / 2 };
      }
      if (handleId === "left" || handleId === "in") {
        return { x: x - 1, y: y + height / 2 };
      }
      if (handleId === "right") {
        return { x: x - 1, y: y + height / 2 + 14 };
      }
      if (String(handleId).startsWith("in-")) {
        const idx = Number(handleId.split("-")[1] || 0);
        return { x: x - 1, y: y + 28 + idx * 22 };
      }
      return { x: x - 1, y: y + height / 2 };
    },
    shortenPathEndForArrow(start, end, controlBeforeEnd, arrowLen = 8) {
      const tx = end.x - controlBeforeEnd.x;
      const ty = end.y - controlBeforeEnd.y;
      const tlen = Math.hypot(tx, ty);
      if (!tlen) return { ...end };
      const totalDist = Math.hypot(end.x - start.x, end.y - start.y);
      const inset = Math.min(arrowLen, Math.max(4, totalDist * 0.15));
      return {
        x: end.x - (tx / tlen) * inset,
        y: end.y - (ty / tlen) * inset,
      };
    },
    edgePath(edge) {
      void this.viewport.zoom;
      void this.viewport.x;
      void this.viewport.y;
      const start = this.handlePosition(edge.source, edge.sourceHandle || "out", "source");
      const end = this.handlePosition(edge.target, edge.targetHandle, "target");
      if (!start || !end) return "";
      const dx = Math.abs(end.x - start.x) * 0.5;
      const cp2 = { x: end.x - dx, y: end.y };
      const pathEnd = this.shortenPathEndForArrow(start, end, cp2);
      return `M ${start.x} ${start.y} C ${start.x + dx} ${start.y}, ${cp2.x} ${cp2.y}, ${pathEnd.x} ${pathEnd.y}`;
    },
    screenToFlow(clientX, clientY) {
      const canvas = this.$refs.canvas && this.$refs.canvas.$el ? this.$refs.canvas.$el : this.$refs.canvas;
      if (!canvas) return { x: 0, y: 0 };
      const rect = canvas.getBoundingClientRect();
      const { x, y, zoom } = this.viewport;
      return {
        x: (clientX - rect.left - x) / zoom,
        y: (clientY - rect.top - y) / zoom,
      };
    },
    onCanvasPanStart(e) {
      if (e.button !== 0) return;
      if (this.isInteractiveTarget(e.target)) return;
      e.preventDefault();
      this.panning = true;
      this.panStart = { x: e.clientX, y: e.clientY, vx: this.viewport.x, vy: this.viewport.y };
      this.selectedNodeId = null;
    },
    onNodeShellMouseDown(e, node) {
      if (e.button !== 0) return;
      if (this.isInteractiveTarget(e.target)) return;
      if (e.target.closest("[data-flow-handle]")) return;
      this.onNodeDragStart(e, node);
    },
    isInteractiveTarget(el) {
      if (!el || !el.closest) return false;
      return !!el.closest(
        "input, textarea, select, button, .v-input, .v-input__slot, .v-select, .v-list, .v-menu, label"
      );
    },
    onNodeDragStart(e, node) {
      if (e.button !== 0) return;
      e.preventDefault();
      const flowPos = this.screenToFlow(e.clientX, e.clientY);
      this.draggingNode = {
        id: node.id,
        offsetX: flowPos.x - node.position.x,
        offsetY: flowPos.y - node.position.y,
      };
      this.selectedNodeId = node.id;
    },
    onWindowMouseMove(e) {
      if (this.panning && this.panStart) {
        this.viewport.x = this.panStart.vx + (e.clientX - this.panStart.x);
        this.viewport.y = this.panStart.vy + (e.clientY - this.panStart.y);
      }
      if (this.draggingNode) {
        const flowPos = this.screenToFlow(e.clientX, e.clientY);
        const node = this.flowNodes.find((n) => n.id === this.draggingNode.id);
        if (node) {
          node.position.x = flowPos.x - this.draggingNode.offsetX;
          node.position.y = flowPos.y - this.draggingNode.offsetY;
          this.edgeLayoutKey += 1;
        }
      }
      if (this.connecting) {
        this.connectMouse = this.screenToFlow(e.clientX, e.clientY);
      }
    },
    onWindowMouseUp(e) {
      if (this.connecting) {
        const target = document.elementFromPoint(e.clientX, e.clientY);
        const handleEl = target && target.closest ? target.closest("[data-flow-handle]") : null;
        if (handleEl) {
          const nodeId = handleEl.getAttribute("data-node-id");
          const handleId = handleEl.getAttribute("data-handle-id");
          const role = handleEl.getAttribute("data-handle-role");
          this.completeConnection(nodeId, handleId, role);
        }
        this.connecting = null;
        this.connectMouse = null;
      }
      if (this.draggingNode) {
        this.emitPassos();
      }
      this.panning = false;
      this.panStart = null;
      this.draggingNode = null;
    },
    onHandleMouseDown(e, nodeId, handleId, role) {
      this.connecting = { nodeId, handleId, role };
      this.connectMouse = this.screenToFlow(e.clientX, e.clientY);
    },
    isHandleActive(nodeId, handleId, role) {
      if (!this.connecting) return false;
      return this.connecting.nodeId === nodeId && this.connecting.handleId === handleId && this.connecting.role === role;
    },
    completeConnection(targetNodeId, targetHandleId, targetRole) {
      if (!this.connecting) return;
      let sourceId;
      let sourceHandle;
      let targetId;
      let targetHandle;

      if (this.connecting.role === "source") {
        sourceId = this.connecting.nodeId;
        sourceHandle = this.connecting.handleId;
        targetId = targetNodeId;
        targetHandle = targetHandleId;
      } else if (targetRole === "source") {
        sourceId = targetNodeId;
        sourceHandle = "out";
        targetId = this.connecting.nodeId;
        targetHandle = this.connecting.handleId;
      } else {
        return;
      }

      if (!sourceId || !targetId || sourceId === targetId) return;
      const sourceNode = this.flowNodes.find((n) => n.id === sourceId);
      const targetNode = this.flowNodes.find((n) => n.id === targetId);
      if (!sourceNode || !targetNode) return;
      if (targetNode.type === "data") return;

      this.flowEdges = this.flowEdges.filter(
        (e) => !(e.target === targetId && e.targetHandle === targetHandle)
      );

      this.flowEdges.push({
        id: `e-${sourceId}-${targetId}-${targetHandle}`,
        source: sourceId,
        target: targetId,
        sourceHandle: sourceHandle || "out",
        targetHandle,
      });

      if (targetNode.type === "output") {
        const src = this.flowNodes.find((n) => n.id === sourceId);
        if (src) targetNode.data.label = src.data.saida_alias || this.outputLabel;
      }

      this.emitPassos();
    },
    addNodeAtCenter(tipoPasso) {
      const canvas = this.$refs.canvas && this.$refs.canvas.$el ? this.$refs.canvas.$el : null;
      const center = canvas
        ? this.screenToFlow(canvas.getBoundingClientRect().left + canvas.clientWidth / 2, canvas.getBoundingClientRect().top + canvas.clientHeight / 2)
        : { x: 200, y: 150 };
      this.insertNode(tipoPasso, center.x - 100, center.y - 50);
    },
    addNodeFromContext(tipoPasso) {
      this.insertNode(tipoPasso, this.contextMenu.flowX, this.contextMenu.flowY);
      this.closeContextMenu();
    },
    insertNode(tipoPasso, x, y) {
      const passoNodes = this.flowNodes.filter((n) => n.type !== "output");
      const node = createFlowNodeFromTipo(tipoPasso, passoNodes.length);
      this.ensureGrandezaDef(node);
      node.position = { x, y };
      const outputIdx = this.flowNodes.findIndex((n) => n.type === "output");
      if (outputIdx >= 0) {
        this.flowNodes.splice(outputIdx, 0, node);
      } else {
        this.flowNodes.push(node);
        this.flowNodes.push(createEmptyOutputNode(this.outputLabel));
      }
      this.selectedNodeId = node.id;
      this.emitPassos();
    },
    deleteNode(nodeId) {
      if (nodeId === "flow-output") return;
      const passoNodes = this.flowNodes.filter((n) => n.type !== "output");
      if (passoNodes.length <= 1 && passoNodes.some((n) => n.id === nodeId)) return;
      this.flowNodes = this.flowNodes.filter((n) => n.id !== nodeId);
      this.flowEdges = this.flowEdges.filter((e) => e.source !== nodeId && e.target !== nodeId);
      if (this.selectedNodeId === nodeId) this.selectedNodeId = null;
      this.emitPassos();
    },
    deleteSelectedNode() {
      if (this.selectedNodeId) this.deleteNode(this.selectedNodeId);
    },
    onCanvasContextMenu(e) {
      const flowPos = this.screenToFlow(e.clientX, e.clientY);
      this.contextMenu = { visible: true, x: e.clientX, y: e.clientY, flowX: flowPos.x, flowY: flowPos.y };
    },
    onNodeContextMenu(e, node) {
      this.selectedNodeId = node.id;
      const flowPos = this.screenToFlow(e.clientX, e.clientY);
      this.contextMenu = { visible: true, x: e.clientX, y: e.clientY, flowX: flowPos.x, flowY: flowPos.y };
    },
    closeContextMenu() {
      this.contextMenu.visible = false;
    },
    zoomIn() {
      this.viewport.zoom = Math.min(2, this.viewport.zoom + 0.1);
      this.refreshEdgeLayout();
    },
    zoomOut() {
      this.viewport.zoom = Math.max(0.4, this.viewport.zoom - 0.1);
      this.refreshEdgeLayout();
    },
    resetViewport() {
      this.viewport = { x: 40, y: 40, zoom: 1 };
      this.refreshEdgeLayout();
    },
    fitView() {
      const nodes = this.flowNodes.filter((n) => n.type !== "output");
      if (!nodes.length) {
        this.resetViewport();
        return;
      }
      const xs = nodes.map((n) => n.position.x);
      const ys = nodes.map((n) => n.position.y);
      const minX = Math.min(...xs);
      const minY = Math.min(...ys);
      this.viewport = { x: 40 - minX, y: 40 - minY, zoom: 1 };
      this.refreshEdgeLayout();
    },
    autoLayout() {
      const graph = layoutFlowGraph(this.flowNodes, this.flowEdges);
      this.flowNodes = graph.nodes;
      this.$nextTick(() => {
        this.fitView();
        this.refreshEdgeLayout();
      });
      this.emitPassos();
    },
    onWheel(e) {
      const delta = e.deltaY > 0 ? -0.08 : 0.08;
      this.viewport.zoom = Math.min(2, Math.max(0.4, this.viewport.zoom + delta));
      this.refreshEdgeLayout();
    },
  },
};
</script>

<style scoped>
.calculo-flow-editor {
  height: 480px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.calculo-flow-main {
  min-width: 0;
}

.calculo-flow-toolbar {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  background: #fafafa;
  flex-shrink: 0;
}

.flow-field {
  font-size: 12px;
}

.flow-field >>> .v-input__slot {
  min-height: 32px !important;
}

.flow-alias >>> input {
  font-family: "Roboto Mono", "Consolas", monospace;
  font-size: 12px;
}

.flow-operator {
  max-width: 90px;
}

.flow-operator-logic {
  width: 100%;
}

.flow-operator-logic >>> .v-select__selections {
  min-width: 0;
}

.flow-operator-logic >>> .v-select__selection--comma {
  overflow: visible;
  text-overflow: unset;
  white-space: normal;
}

.flow-grandeza-resumo {
  font-family: "Roboto Mono", "Consolas", monospace;
  line-height: 1.3;
  word-break: break-word;
}

.flow-operand-hints {
  line-height: 1.6;
  min-height: 20px;
}

.flow-operand-empty {
  color: #b0bec5;
  font-style: italic;
}

.flow-output-label {
  color: #5e35b1;
  font-family: "Roboto Mono", "Consolas", monospace;
  font-size: 15px !important;
}

.flow-node-anchor {
  position: relative;
}

.flow-pane-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 4000px;
  height: 3000px;
  z-index: 0;
  cursor: grab;
}

.flow-pane-bg:active {
  cursor: grabbing;
}

.flow-handle-wrap {
  position: absolute;
  width: 12px;
  height: 12px;
  right: -7px;
  top: 50%;
  margin-top: -6px;
  z-index: 2;
}

.flow-handle-wrap--left,
.flow-handle-wrap--in {
  right: auto;
  left: -7px;
}

.flow-handle-wrap--right {
  right: auto;
  left: -7px;
  margin-top: 8px;
}
</style>
